// スタートキャラ（少年・少女の共通 GLB ＋ kai/lilia 由来ロール値）のパッケージ生成ロジック。
// Node 版 scaffold.mjs（sevenfold 全コピー方式）と、ブラウザ完結版 backend（共有ランタイム方式）の
// 双方から使う純粋関数。node 組み込みへ一切依存しない（＝esbuild でそのままブラウザへバンドル可能）。
// 入力の defaultChars（dc）は config/default-characters.json の内容そのもの。

import { withEssentialInitialItems } from './essential-gameplay-items.mjs';

const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

/** 主人公→相方の順の [{modelKey, roleKey, id, name, description}]。 */
export function orderedCharacters(opts, dc) {
    const modelOrder = opts.protagonist === 'girl' ? ['girl', 'boy'] : ['boy', 'girl'];
    return modelOrder.map((modelKey, i) => ({
        modelKey,
        roleKey: i === 0 ? 'protagonist' : 'partner',
        id: opts.characters[modelKey].id,
        name: opts.characters[modelKey].name,
        description: dc.roles[i === 0 ? 'protagonist' : 'partner'].description,
    }));
}

/** maps/_shared/characters.json の内容（2 キャラ構成）を組み立てる。 */
export function buildCharactersJson(opts, dc) {
    const modelOrder = opts.protagonist === 'girl' ? ['girl', 'boy'] : ['boy', 'girl'];
    const roleOrder = ['protagonist', 'partner'];
    const characters = modelOrder.map((modelKey, i) => {
        const ms = dc.modelSets[modelKey];
        const role = dc.roles[roleOrder[i]];
        const c = opts.characters[modelKey];
        return {
            id: c.id,
            name: c.name.ja,
            modelPathIdle: ms.modelPathIdle,
            modelPathWalk: ms.modelPathWalk,
            modelPathAttack: ms.modelPathAttack,
            displayHeight: ms.displayHeight,
            // Meshy APIへ渡す物理身長（m）。ゲーム内正規化用displayHeightとは別単位。
            riggingHeightMeters: ms.riggingHeightMeters,
            headLabelOffsetY: ms.headLabelOffsetY,
            collider: ms.collider,
            style: ms.style,
            attackRange: role.attackRange,
            startMapId: opts.startMapId,
            stats: role.stats,
            // 全作品共通の基本アイテムは、設定ファイル側で欠落・減少しても下限を保証する。
            initialItems: withEssentialInitialItems(role.initialItems),
            initialEquipment: role.initialEquipment,
        };
    });
    return { formatVersion: '1.0', characters };
}

/** ②直後のplaceholderマップ用。宝箱未生成でもエンジンの必須JSON取得を404にしない。 */
export function buildEmptyTreasuresJson(mapId) {
    return { formatVersion: '1.0', mapId, treasures: [] };
}

/**
 * skills.json の characterSkills を旧キャラ（kai/lilia 相当）→ 新 2 キャラへ付け替える（破壊的）。
 * @returns {string[]} 警告メッセージ（元キャラの characterSkills が無かった場合）
 */
export function patchSkillsCharacterSkills(skills, opts, dc) {
    const chars = orderedCharacters(opts, dc);
    const oldSkills = skills.characterSkills ?? {};
    const nextSkills = {};
    const warnings = [];
    for (const c of chars) {
        const fromId = dc.roles[c.roleKey].skillsFromCharacterId;
        nextSkills[c.id] = oldSkills[fromId] ?? [];
        if (!(fromId in oldSkills)) {
            warnings.push(`skills.json: 元キャラ ${fromId} の characterSkills が見つからないため ${c.id} は空配列にしました。`);
        }
    }
    skills.characterSkills = nextSkills;
    return warnings;
}

/**
 * 1 言語分のロケール（messages/exact）へ、設定案で編集できる作品情報
 * （タイトル・サブタイトル・原作クレジット）だけを反映する（破壊的）。
 * キャラ関連キーや⑤で取り込んだ翻訳には触れないため、「ゲーム設定案を編集」の
 * 直接反映（後工程データを初期化しない）にも安全に使える。
 * @param identity { title: {ja,en}, subtitle?: {ja,en}, description?: {ja,en}, originalUrl? }
 * @param {'ja'|'en'} lang
 */
export function patchLocaleGameIdentity(locale, identity, lang) {
    const msg = locale.messages ?? (locale.messages = {});
    const startMenu = msg.startMenu ?? (msg.startMenu = {});
    const game = msg.game ?? (msg.game = {});
    const title = identity.title[lang];
    const subtitle = identity.subtitle?.[lang] || identity.description?.[lang] || title;
    startMenu.title = title;
    startMenu.subtitle = subtitle;
    game.title = title;
    game.subtitle = subtitle;
    if (identity.originalUrl) {
        startMenu.credit = lang === 'ja'
            ? `原作：${title}`
            : `Original work: ${title}`;
    } else {
        delete startMenu.credit;
    }
    if (lang === 'en') {
        const exact = locale.exact ?? (locale.exact = {});
        const addExact = (ja, en) => {
            if (ja && en && ja !== en) exact[ja] = en;
        };
        addExact(identity.title.ja, identity.title.en);
        addExact(identity.description?.ja, identity.description?.en);
        addExact(identity.subtitle?.ja, identity.subtitle?.en);
    }
    return locale;
}

/**
 * 1 言語分のロケール（messages/exact）へ、タイトル・キャラ名・選択ボタン title を反映する（破壊的）。
 * 旧 3 キャラ分のキーは削除する。en のときは exact に日本語→英語対訳を追加する。
 * @param {'ja'|'en'} lang
 * @param {string[]} extraOldIds 追加で削除する旧キャラ ID（ブラウザ版の②再実行時、
 *   前回パッケージのキャラ ID 残骸を掃除するために使う。省略時は dc.oldCharacterIds のみ）
 */
export function patchCharacterLocale(locale, opts, dc, lang, extraOldIds = []) {
    const chars = orderedCharacters(opts, dc);
    patchLocaleGameIdentity(locale, opts, lang);
    const msg = locale.messages;
    msg.character = msg.character ?? {};
    for (const oldId of [...dc.oldCharacterIds, ...extraOldIds]) {
        delete msg.character[oldId];
        delete msg.startMenu[`select${capitalize(oldId)}Title`];
    }
    for (const c of chars) {
        msg.character[c.id] = { name: c.name[lang], description: c.description[lang] };
        // 置換値は関数で渡す（名前に $& 等の置換メタ文字が含まれても文字どおり展開するため）
        msg.startMenu[`select${capitalize(c.id)}Title`] = dc.selectTitleTemplate[lang].replaceAll('{name}', () => c.name[lang]);
    }
    if (lang === 'en') {
        locale.exact = locale.exact ?? {};
        const addExact = (ja, en) => {
            if (ja && en && ja !== en) locale.exact[ja] = en;
        };
        for (const c of chars) {
            addExact(c.name.ja, c.name.en);
            addExact(c.description.ja, c.description.en);
        }
    }
    return locale;
}
